import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {CustomizedContainerStyled, BubblePointsContainerStyled, InfoContainerStyled} from "../../../styled/Resume";
import {IoAlertCircleOutline} from "react-icons/io5";
import { FaEye, FaChevronDown } from 'react-icons/fa';
import {useTheme} from "../../UI/Theme/ThemeContext";
import ProgressBar from "../Tools/ProgressBar";
import DataOutlet from "./DataOutlet";
import TutorialPopover from '../../UI/TutorialPopover/TutorialPopover';
import { getTutorialTipMeta } from '../../../utils/tutorialApi';


const CustomizeStep = (props) => {

    const {
        bubblePoints,
        setActiveStep,
        DragDropHandleDrop,
        DragDropRef,
        DragDropFileError,
        DragDropFile,
        DragDropHandleUploadFile,
        clearManualUpload,
        optimizeBasicInfo,
        optimizeHandleInputChange,
        optimizeJobConnectType,
        optimizeJobTypesArray,
        optimizeHandleJobConnectType,
        optimizeSetAdditionalInfo,
        optimizeAdditionalInfo,
        handleFileResumeOptimize,
        onSubmitCreate,
        addlInfoImagePreviews,
        addlInfoFileInputRef,
        triggerAddlInfoSelect,
        handleAddlInfoFileSelect,
        removeSingleAddlInfoImage,
        basicLinkInfo,
        basicHandleAddLinks,
        basicHandleDeleteLinks,
        basicHandleLinkChange,
        eduArray,
        handleEduExpChange,
        handleDeleteEduInfo,
        handleAddEduInfo,
        workExpArray,
        handleWorkExpChange,
        handleDeleteExp,
        handleAddExp,
        skills,
        handleSkillChange,
        handleDeleteSkill,
        handleAddSkill,
        awardArray,
        handleAwardChange,
        handleDeleteAward,
        handleAddAward,
        publications,
        handlePubChange,
        handleDeletePublication,
        handleAddPublication,
        languages,
        handleLangChange,
        languageStore,
        langLevelsArray,
        handleDeleteLang,
        handleAddLang,
        interests,
        setInterestsArray,
        handleInterestChange,
        handleDeleteInterests,
        handleAddInterests,
        basicInfoForward,
        eduInfoForwardOrBackward,
        workExpForwardOrBackward,
        skillsForwardOrBackward,
        certsForwardOrBackward,
        pubForwardOrBackward,
        langForwardOrBackward,
        interestsBackward,
        existingResumes,
        existingResumeSelectedIndex,
        onExistingResumeSelect,
        onExistingResumePreview,
        storedResumeSelected,
        selectedStoredResumeName,
    } = props

    const {themeName} = useTheme();
    // Tutorial popover refs & state (two tips: bubble points and create-from-scratch)
    const bubbleRef = useRef(null);
    const createNewRef = useRef(null);
    const [currentTip, setCurrentTip] = useState(0);
    const [sequenceCompleted, setSequenceCompleted] = useState(false);
    const [tipMeta, setTipMeta] = useState({});
    const [bubbleShownOnce, setBubbleShownOnce] = useState(false);
    const [createShownOnce, setCreateShownOnce] = useState(false);
    const RESUME_TIP_ID = 'resume:customize:cta-sequence';
    const [infoOpen, setInfoOpen] = useState(false);
    const collapsibleRef = useRef(null);
    const [collapsibleHeight, setCollapsibleHeight] = useState('0px');

    useEffect(() => {
        // Update maxHeight to scrollHeight when opening for smooth animation
        if (collapsibleRef.current) {
            if (infoOpen) {
                setCollapsibleHeight(`${collapsibleRef.current.scrollHeight}px`);
            } else {
                setCollapsibleHeight('0px');
            }
        }
    }, [infoOpen]);

    // Load persisted tip meta once
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const meta = await getTutorialTipMeta(RESUME_TIP_ID);
                if (!mounted) return;
                setTipMeta(meta || {});
            } catch (e) {}
        })();
        return () => { mounted = false; };
    }, []);

    // derive a simple indicator that the parent/main data has populated this component
    const hasInitialData = useMemo(() => {
        if (typeof bubblePoints === 'number') return true;
        if (Array.isArray(eduArray) && eduArray.length > 0) return true;
        if (Array.isArray(workExpArray) && workExpArray.length > 0) return true;
        if (Array.isArray(skills) && skills.length > 0) return true;
        if (Array.isArray(publications) && publications.length > 0) return true;
        if (Array.isArray(languages) && languages.length > 0) return true;
        return false;
    }, [bubblePoints, eduArray, workExpArray, skills, publications, languages]);

    // Attempt to show first tip (bubble) shortly after the main data has populated
    useEffect(() => {
        // anchor element reference for bubble container
        bubbleRef.current = document.querySelector('.bubblePoints .pointsContainer') || null;
        // anchor for create-from-scratch tab
        createNewRef.current = document.querySelector('.headerOutlet .fromNew') || null;

        if (!sequenceCompleted && !tipMeta?.dontShow && hasInitialData) {
            // Wait ~10s after the main populate to give the UI time to mount fully
            const t = setTimeout(() => {
                if (!bubbleShownOnce) {
                    setCurrentTip(1);
                    setBubbleShownOnce(true);
                }
            }, 20000);
            return () => clearTimeout(t);
        }
        // otherwise, do nothing until hasInitialData becomes true
    }, [tipMeta, sequenceCompleted, bubbleShownOnce, hasInitialData]);

    const scheduleNextTip = (idx) => {
        // schedule following tip after 6s
        if (idx === 1) {
            setTimeout(async () => {
                try {
                    const fresh = await getTutorialTipMeta(RESUME_TIP_ID);
                    const dont = fresh && fresh.dontShow;
                    if (!dont && !sequenceCompleted && createNewRef.current && !createShownOnce) {
                        setCurrentTip(2);
                        setCreateShownOnce(true);
                    }
                } catch (e) {
                    if (!tipMeta?.dontShow && !sequenceCompleted && createNewRef.current && !createShownOnce) {
                        setCurrentTip(2);
                        setCreateShownOnce(true);
                    }
                }
            }, 6000);
        }
    };

    const handleCloseBubbleTip = () => {
        setCurrentTip(0);
        scheduleNextTip(1);
    };

    const handleCloseCreateTip = () => {
        setCurrentTip(0);
        setSequenceCompleted(true);
    };

    return (
        <CustomizedContainerStyled>
            <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: '1rem', flexDirection: 'column', paddingLeft: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <BubblePointsContainerStyled theme={themeName} style={{ flex: 1, margin: 0, cursor: 'pointer'}} onClick={() => setInfoOpen(prev => !prev)}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <p className="subHeading">Optimize resumes to gain points</p>
                            {/* Clickable info icon moved to the header far right */}
                        </div>
                        <div className="bubblePoints">
                            <div className="pointsContainer">
                                <span className="fillerInfo">
                                    <IoAlertCircleOutline color="rgba(242, 72, 34, 1)"/>
                                    <p className="fillerText">Bubble Points Gained:</p>
                                </span>
                                <ProgressBar value={typeof bubblePoints === 'number' ? bubblePoints : 0}/>
                            </div>
                            {/* keep the ol but it will be hidden inside the collapsible area */}
                        </div>
                    </BubblePointsContainerStyled>

                    {/* clickable alert at far right that reveals the rest */}
                    <button
                        aria-expanded={infoOpen}
                        onClick={() => setInfoOpen(prev => !prev)}
                        title={infoOpen ? 'Hide details' : 'Show details'}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 8,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        className="resume-info-toggle"
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#3E8F93', fontSize: 18 }}>
                            <FaChevronDown style={{ transform: `rotate(${infoOpen ? 180 : 0}deg)`, transition: 'transform 200ms' }} />
                        </span>
                    </button>
                </div>

                {/* Collapsible area containing the ol and the info container */}
                <div
                    ref={collapsibleRef}
                    style={{
                        maxHeight: collapsibleHeight,
                        overflow: 'hidden',
                        transition: 'max-height 300ms ease',
                        width: '100%'
                    }}
                >
                    <div style={{ paddingTop: 12 }}>
                        <BubblePointsContainerStyled theme={themeName} style={{ marginTop: 0 }}>
                            <div style={{ paddingTop: 0 }}>
                                <ol>
                                    <li>Gain 100%, get a FREE WEEK/MONTH.</li>
                                    <li>Free trial users not eligible.</li>
                                    <li>For PER WEEK/MONTH subscribers only.</li>
                                </ol>
                            </div>
                        </BubblePointsContainerStyled>

                        <InfoContainerStyled theme={themeName} style={{ marginTop: 8 }}>
                            <span className="alertIcon" style={{ cursor: 'default' }}>
                                <FaEye style={{ color: '#3E8F93' }} />
                            </span>
                            <ul>
                                <li>The + and - buttons are to add and delete applicable input fields or sections</li>
                                <li>All fields with * are required</li>
                                <li>
                                    Location on this page appears on your resume. For{' '}
                                    <Link to="/user/dashboard/profile" style={{ color: '#5fbec5', textDecoration: 'underline' }}>
                                        job connect location
                                    </Link>
                                    , change{' '}
                                    <Link to="/user/dashboard/profile" style={{ color: '#5fbec5', textDecoration: 'underline' }}>
                                        Location in Profile
                                    </Link>
                                </li>
                                <li>Have Questions? Please use the chatbot</li>
                            </ul>
                        </InfoContainerStyled>
                    </div>
                </div>
            </div>

            <DataOutlet
                setActiveStep={setActiveStep}
                DragDropHandleDrop={DragDropHandleDrop}
                DragDropRef={DragDropRef}
                DragDropFileError={DragDropFileError}
                DragDropFile={DragDropFile}
                DragDropHandleUploadFile={DragDropHandleUploadFile}
                clearManualUpload={clearManualUpload}
                optimizeBasicInfo={optimizeBasicInfo}
                optimizeHandleInputChange={optimizeHandleInputChange}
                optimizeJobConnectType={optimizeJobConnectType}
                optimizeJobTypesArray={optimizeJobTypesArray}
                optimizeHandleJobConnectType={optimizeHandleJobConnectType}
                optimizeSetAdditionalInfo={optimizeSetAdditionalInfo}
                optimizeAdditionalInfo={optimizeAdditionalInfo}
                handleFileResumeOptimize={handleFileResumeOptimize}
                onSubmitCreate={onSubmitCreate}
                addlInfoImagePreviews={addlInfoImagePreviews}
                addlInfoFileInputRef={addlInfoFileInputRef}
                triggerAddlInfoSelect={triggerAddlInfoSelect}
                handleAddlInfoFileSelect={handleAddlInfoFileSelect}
                removeSingleAddlInfoImage={removeSingleAddlInfoImage}
                basicLinkInfo={basicLinkInfo}
                basicHandleAddLinks={basicHandleAddLinks}
                basicHandleDeleteLinks={basicHandleDeleteLinks}
                basicHandleLinkChange={basicHandleLinkChange}
                eduArray={eduArray}
                handleEduExpChange={handleEduExpChange}
                handleDeleteEduInfo={handleDeleteEduInfo}
                handleAddEduInfo={handleAddEduInfo}
                workExpArray={workExpArray}
                handleWorkExpChange={handleWorkExpChange}
                handleDeleteExp={handleDeleteExp}
                handleAddExp={handleAddExp}
                skills={skills}
                handleSkillChange={handleSkillChange}
                handleDeleteSkill={handleDeleteSkill}
                handleAddSkill={handleAddSkill}
                awardArray={awardArray}
                handleAwardChange={handleAwardChange}
                handleDeleteAward={handleDeleteAward}
                handleAddAward={handleAddAward}
                publications={publications}
                handlePubChange={handlePubChange}
                handleDeletePublication={handleDeletePublication}
                handleAddPublication={handleAddPublication}
                languages={languages}
                handleLangChange={handleLangChange}
                languageStore={languageStore}
                langLevelsArray={langLevelsArray}
                handleDeleteLang={handleDeleteLang}
                handleAddLang={handleAddLang}
                interests={interests}
                setInterestsArray={setInterestsArray}
                handleInterestChange={handleInterestChange}
                handleDeleteInterests={handleDeleteInterests}
                handleAddInterests={handleAddInterests}
                basicInfoForward={basicInfoForward}
                eduInfoForwardOrBackward={eduInfoForwardOrBackward}
                workExpForwardOrBackward={workExpForwardOrBackward}
                skillsForwardOrBackward={skillsForwardOrBackward}
                certsForwardOrBackward={certsForwardOrBackward}
                pubForwardOrBackward={pubForwardOrBackward}
                langForwardOrBackward={langForwardOrBackward}
                interestsBackward={interestsBackward}
                existingResumes={existingResumes}
                existingResumeSelectedIndex={existingResumeSelectedIndex}
                onExistingResumeSelect={onExistingResumeSelect}
                onExistingResumePreview={onExistingResumePreview}
                storedResumeSelected={storedResumeSelected}
                selectedStoredResumeName={selectedStoredResumeName}
                
            />

                {/* Tutorial popovers for CustomizeResume: bubble points and create-from-scratch */}
                <TutorialPopover
                    show={currentTip === 1 && !sequenceCompleted}
                    tipId={RESUME_TIP_ID}
                    anchorRef={bubbleRef}
                    title={'Bubble Points'}
                    description={'Tap to view how points are earned and what rewards are available. Click the button to learn how to earn more.'}
                    primaryLabel={'View details'}
                    onClose={() => { handleCloseBubbleTip(); }}
                    onPrimary={() => {
                        // open the collapsible area to show details
                        try { setCollapsibleHeight(collapsibleRef.current ? `${collapsibleRef.current.scrollHeight}px` : '0px'); } catch(_) {}
                        setInfoOpen(true);
                    }}
                />

                <TutorialPopover
                    show={currentTip === 2 && !sequenceCompleted}
                    tipId={RESUME_TIP_ID}
                    anchorRef={createNewRef}
                    title={'Create from scratch'}
                    description={'Open Create if you do not have a resume. Answer a few questions and I will generate a standard resume for you. Typically takes 3 minutes'}
                    primaryLabel={'Open Create'}
                    onClose={() => { handleCloseCreateTip(); }}
                    onPrimary={() => {
                        // try to click the header tab to switch to create flow
                        const el = document.querySelector('.headerOutlet .fromNew');
                        if (el) el.click();
                    }}
                />

        </CustomizedContainerStyled>

    )
}

export default CustomizeStep